<template>
  <v-container fluid class="pet-metrics">
    <h2 class="page-title mb-1">Mascotas protegidas</h2>
    <p class="page-caption mb-4">
      Cuántas mascotas protege Pawer hoy y cuántas podría proteger si todos los usuarios con acceso
      activan sus mascotas.<template v-if="data"> Al {{ data.asOf }}.</template>
    </p>

    <div v-if="loadingData && !data" class="text-center py-10">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <v-alert v-else-if="loadError" type="error" outlined class="load-error">
      No se pudieron cargar las métricas de mascotas.
      <template v-slot:append>
        <v-btn small outlined color="error" @click="loadData">Reintentar</v-btn>
      </template>
    </v-alert>

    <template v-else-if="data">
      <!-- Resumen -->
      <v-row dense class="mb-2">
        <v-col cols="12" sm="6" md="3" class="d-flex">
          <CardResumeCompanyDetails
            class="flex-grow-1 summary-card"
            titulo="Usuarios cargados"
            :firstValue="fmt(data.totals.usersWithAccess)"
            :showCsvButton="false"
            stacked
          />
        </v-col>
        <v-col cols="12" sm="6" md="3" class="d-flex">
          <CardResumeCompanyDetails
            class="flex-grow-1 summary-card"
            titulo="Membresías disponibles"
            :firstValue="fmt(data.totals.potentialPets)"
            secondValue="Incluye las mascotas activas"
            :showCsvButton="false"
            stacked
          />
        </v-col>
        <v-col cols="12" sm="6" md="3" class="d-flex">
          <CardResumeCompanyDetails
            class="flex-grow-1 summary-card"
            titulo="Mascotas activas"
            :firstValue="fmt(data.totals.activePets)"
            :showCsvButton="false"
            stacked
          />
        </v-col>
        <v-col cols="12" sm="6" md="3" class="d-flex">
          <CardResumeCompanyDetails
            class="flex-grow-1 summary-card"
            titulo="Penetración"
            :firstValue="penetrationLabel"
            :showCsvButton="false"
            stacked
          />
        </v-col>
      </v-row>

      <!-- Avisos de calidad de datos -->
      <v-alert v-if="anomalousQuotas.length" type="warning" outlined dense class="mb-2">
        Cupos que se ven anómalos (se cuentan tal cual como bolsa; revisar el dato):
        <span v-for="(c, i) in anomalousQuotas" :key="c.contratoId">
          {{ c.codigo }} {{ c.company }} ({{ fmt(c.membershipQuota) }})<template v-if="i < anomalousQuotas.length - 1">, </template>
        </span>
      </v-alert>

      <!-- Gráfico -->
      <v-card outlined class="pa-4 mb-4">
        <div class="d-flex flex-wrap align-center mb-2">
          <h3 class="section-title">Evolución mensual</h3>
          <v-spacer></v-spacer>
          <v-btn-toggle v-model="chartLines" mandatory dense class="mr-2">
            <v-btn small value="both">Ambas</v-btn>
            <v-btn small value="active">Mascotas activas</v-btn>
            <v-btn small value="available">Membresías disponibles</v-btn>
          </v-btn-toggle>
          <v-btn-toggle v-model="range" mandatory dense @change="loadData">
            <v-btn small :value="12">12 meses</v-btn>
            <v-btn small :value="24">24 meses</v-btn>
            <v-btn small value="all">Todo</v-btn>
          </v-btn-toggle>
        </div>
        <PetProtectionTrendChart
          :series="data.series"
          :lines="chartLines"
          :reconstruction-note="data.reconstructionNote"
        />
      </v-card>

      <!-- Por línea -->
      <v-card outlined class="mb-4">
        <v-card-title class="section-title">Por línea de negocio</v-card-title>
        <div class="table-scroll">
          <v-data-table
            :headers="lineHeaders"
            :items="data.byLine"
            :loading="loadingData"
            item-key="lineaNegocio"
            hide-default-footer
            disable-pagination
            dense
          >
            <template v-slot:[`item.activePets`]="{ item }">{{ fmt(item.activePets) }}</template>
            <template v-slot:[`item.potentialPets`]="{ item }">{{ fmt(item.potentialPets) }}</template>
            <template v-slot:[`item.usersWithAccess`]="{ item }">{{ fmt(item.usersWithAccess) }}</template>
            <template v-slot:[`body.append`]>
              <tr class="font-weight-bold">
                <td>Total Pawer</td>
                <td class="text-right">{{ fmt(data.totals.usersWithAccess) }}</td>
                <td class="text-right">{{ fmt(data.totals.potentialPets) }}</td>
                <td class="text-right">{{ fmt(data.totals.activePets) }}</td>
                <td class="text-right">{{ contractsInScope }}</td>
              </tr>
            </template>
          </v-data-table>
        </div>
      </v-card>

      <!-- Por contrato: requiere `pet-protection-metrics:view-contracts` (sin ella el backend
           devuelve la lista vacía). -->
      <v-card v-if="can('pet-protection-metrics:view-contracts')" outlined class="mb-4">
        <v-card-title class="section-title flex-wrap">
          Por contrato
          <v-spacer></v-spacer>
          <v-select
            v-model="lineFilter"
            :items="lineOptions"
            label="Línea"
            dense
            outlined
            hide-details
            clearable
            class="filter-field mr-2"
          ></v-select>
          <v-text-field
            v-model="search"
            label="Buscar código o empresa"
            prepend-inner-icon="mdi-magnify"
            dense
            outlined
            hide-details
            clearable
            class="filter-field"
          ></v-text-field>
        </v-card-title>
        <div class="table-scroll">
          <v-data-table
            :headers="contractHeaders"
            :items="filteredContracts"
            :search="search"
            :loading="loadingData"
            item-key="contratoId"
            sort-by="potentialPets"
            sort-desc
            :items-per-page="25"
            dense
          >
            <template v-slot:[`item.codigo`]="{ item }">
              <router-link v-if="can('contracts:view')" :to="{ path: '/contratos', query: { codigo: item.codigo } }">{{ item.codigo }}</router-link>
              <span v-else>{{ item.codigo }}</span>
            </template>
            <template v-slot:[`item.model`]="{ item }">
              {{ modelLabel(item.model) }}
            </template>
            <template v-slot:[`item.usersWithAccess`]="{ item }">{{ fmt(item.usersWithAccess) }}</template>
            <template v-slot:[`item.membershipLimit`]="{ item }">
              {{ item.membershipLimit == null ? '—' : fmt(item.membershipLimit) }}
            </template>
            <template v-slot:[`item.membershipQuota`]="{ item }">
              {{ item.membershipQuota ? fmt(item.membershipQuota) : '—' }}
            </template>
            <template v-slot:[`item.activePets`]="{ item }">{{ fmt(item.activePets) }}</template>
            <template v-slot:[`item.potentialPets`]="{ item }">{{ fmt(item.potentialPets) }}</template>
            <template v-slot:[`item.flags`]="{ item }">
              <v-chip
                v-for="flag in item.flags"
                :key="flag"
                x-small
                outlined
                :color="flagColor(flag)"
                class="mr-1 mb-1"
              >{{ flagLabel(flag) }}</v-chip>
            </template>
          </v-data-table>
        </div>
      </v-card>

      <!-- Supuestos -->
      <v-expansion-panels flat class="assumptions">
        <v-expansion-panel>
          <v-expansion-panel-header class="section-title">Supuestos del cálculo (a validar)</v-expansion-panel-header>
          <v-expansion-panel-content>
            <ul class="assumption-list">
              <li><strong>Membresías disponibles</strong> = el total que se podría proteger, <strong>incluidas las mascotas activas</strong>. En una bolsa es el cupo; si no hay cupo, usuarios cargados × mascotas por usuario ("Límite de membresías" del contrato; vacío = 1). Nunca es menor que las mascotas activas.</li>
              <li><strong>Sin límite</strong>: un límite de {{ fmt(data.assumptions.unlimitedLimitThreshold) }} o más es el valor que se usa para "sin límite". No se multiplica: cuenta como <strong>1 mascota por usuario</strong> (provisorio).</li>
              <li><strong>Modelos</strong>: Uno a uno = 1 mascota por usuario; Todos × límite = usuarios × mascotas por usuario; Bolsa = el cupo contratado (si la bolsa está superada, cuentan todas las activas).</li>
              <li><strong>Cómo se elige el modelo</strong>: si el contrato tiene cupo cargado ("Membresías contratadas"), es bolsa, en cualquier línea. Sin cupo, Asistencias es uno a uno y el resto todos × límite.</li>
              <li><strong>Usuarios cargados</strong>: usuarios activos cargados en el contrato. En B2C cuenta cualquier usuario del canal (clientes actuales y pasados), porque ahí la marca de visibilidad no indica acceso.</li>
              <li><strong>Cupo anómalo</strong>: cupo ≥ {{ fmt(data.assumptions.anomalousQuotaMin) }} y mayor que {{ data.assumptions.anomalousQuotaRatio }}× los usuarios o las activas. Se cuenta tal cual y se marca para revisar el dato del contrato.</li>
              <li><strong>Fuera de vigencia</strong> (churned, pausados, demos) y <strong>Sin contrato</strong>: suman sus mascotas activas a las membresías disponibles, sin usuarios.</li>
              <li>Southbridge CL-106 entra tal cual.</li>
            </ul>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
  </v-container>
</template>

<script>
import CardResumeCompanyDetails from '@/components/CardResumeCompanyDetails.vue';
import PetProtectionTrendChart from '@/components/PetProtectionTrendChart.vue';
import PetProtectionMetricsService from '@/services/pet-protection-metrics.service';
import { utilsMixin } from '@/mixins/utilsMixin';
import { can } from '@/utils/permissions';
import {
  currentPeriodOf,
  flagLabel,
  formatInt,
  modelLabel,
  rangeFrom
} from '@/utils/pet-protection.utils';

// Las marcas que son supuestos o datos a revisar se destacan; las demás son informativas.
const WARNING_FLAGS = ['quota_anomalous', 'unlimited_limit_assumed_one'];

export default {
  name: 'MetricasMascotas',
  components: { CardResumeCompanyDetails, PetProtectionTrendChart },
  mixins: [utilsMixin],
  data() {
    return {
      loadingData: false,
      loadError: false,
      data: null,
      range: 24,
      chartLines: 'both',
      lineFilter: null,
      search: '',
      lineHeaders: [
        { text: 'Línea', value: 'lineaNegocio' },
        { text: 'Usuarios cargados', value: 'usersWithAccess', align: 'end' },
        { text: 'Membresías disponibles', value: 'potentialPets', align: 'end' },
        { text: 'Mascotas activas', value: 'activePets', align: 'end' },
        { text: 'Contratos vigentes', value: 'contracts', align: 'end' }
      ],
      contractHeaders: [
        { text: 'Contrato', value: 'codigo' },
        { text: 'Empresa', value: 'company' },
        { text: 'Línea', value: 'lineaNegocio' },
        { text: 'Modelo', value: 'model' },
        { text: 'Usuarios cargados', value: 'usersWithAccess', align: 'end' },
        { text: 'Límite / usuario', value: 'membershipLimit', align: 'end' },
        { text: 'Cupo', value: 'membershipQuota', align: 'end' },
        { text: 'Membresías disponibles', value: 'potentialPets', align: 'end' },
        { text: 'Mascotas activas', value: 'activePets', align: 'end' },
        { text: 'Marcas', value: 'flags', sortable: false }
      ]
    };
  },
  computed: {
    contracts() {
      return this.data ? this.data.contracts : [];
    },
    filteredContracts() {
      if (!this.lineFilter) return this.contracts;
      return this.contracts.filter((c) => c.lineaNegocio === this.lineFilter);
    },
    lineOptions() {
      return [...new Set(this.contracts.map((c) => c.lineaNegocio))];
    },
    contractsInScope() {
      return this.data ? this.data.byLine.reduce((sum, l) => sum + l.contracts, 0) : 0;
    },
    anomalousQuotas() {
      return this.contracts.filter((c) => c.flags.includes('quota_anomalous'));
    },
    penetrationLabel() {
      const pct = this.data && this.data.totals.penetrationPct;
      return pct === null || pct === undefined ? '—' : `${Number(pct).toFixed(1).replace('.', ',')}%`;
    }
  },
  created() {
    this.loadData();
  },
  methods: {
    can,
    async loadData() {
      this.loadingData = true;
      this.loadError = false;
      const from = rangeFrom(currentPeriodOf(), this.range);
      const res = await PetProtectionMetricsService.getMetrics({ from });
      if (res.status === 'ok') {
        this.data = res.data;
      } else {
        this.data = null;
        this.loadError = true;
        this.showErrorSnackbar('Error al cargar las métricas de mascotas');
      }
      this.loadingData = false;
    },
    fmt(n) {
      return formatInt(n);
    },
    modelLabel,
    flagLabel,
    flagColor(flag) {
      return WARNING_FLAGS.includes(flag) ? 'warning' : undefined;
    }
  }
};
</script>

<style scoped>
/* Same height whether or not a card has a subtitle, also when they wrap to two rows. */
.summary-card {
  min-height: 100px;
}
.table-scroll {
  width: 100%;
  overflow-x: auto;
}
.filter-field {
  max-width: 260px;
}
.assumption-list li {
  margin-bottom: 6px;
}
@media (max-width: 600px) {
  .filter-field {
    max-width: 100%;
    width: 100%;
    margin: 8px 0 0 0 !important;
  }
}
</style>
